import { useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { INDEX_SPECS } from '@apna/shared'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Pick the instrument whose chain is showing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INDICES ONLY, FOR NOW — 2026-11-01
 *
 * `/instruments/underlyings` returns every underlying with listed option
 * contracts, which is the six indices and about two hundred F&O stocks, and
 * this offered all of them behind an All / Indices / F&O stocks filter.
 * Asked to show the indices alone and hide the rest.
 *
 * NARROWED AT ONE POINT, deliberately, because "for now" is in the request.
 * `INDICES_ONLY` below is the whole change: the fetch is unchanged, the
 * endpoint is unchanged, and flipping it back restores the stocks and the
 * filter chips with them. Pushing the restriction into the request instead
 * would have spread it across a controller and a query for a decision that
 * is explicitly temporary.
 *
 * The chips went with it rather than being left showing one option. A
 * filter that cannot filter is furniture, and it would have implied the
 * other two were a click away.
 *
 * FILTERED IN THE BROWSER, ON PURPOSE.
 *
 * The list is one small request, and filtering locally means keystrokes
 * cost nothing and it stays usable with the network down. A search endpoint
 * per keystroke would also be a per-keystroke query against tens of
 * thousands of contract rows.
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface UnderlyingDto {
  underlying: string
  exchange: 'NSE' | 'BSE'
  kind: 'INDEX' | 'STOCK'
}

/**
 * Hide every underlying that is not an index.
 *
 * One constant so the restriction is one edit to lift. Set it false and the
 * F&O stocks come back — along with the filter chips, which are rendered
 * off the same flag because they only mean something when there is more
 * than one kind of row to choose between.
 */
const INDICES_ONLY = true

/**
 * The three that open first, in this order.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ALPHABETICAL IS NOT NEUTRAL, IT IS JUST A DIFFERENT BIAS
 *
 * The list arrives sorted by name, which put BANKEX at the top and NIFTY
 * fourth. That is an ordering by spelling over an ordering by what is
 * traded: NIFTY, BANKNIFTY and SENSEX carry almost all of the volume on
 * this screen, and the other indices are occasional.
 *
 * APPLIED TO SEARCH RESULTS TOO, which is where it earns the most: typing
 * "NI" matched FINNIFTY before NIFTY, because the match is a substring and
 * F sorts first. A ranking that only held for the unfiltered list would
 * leave the common case — two keystrokes and Enter — landing on the wrong
 * index.
 *
 * NOT A SEPARATE SECTION. A pinned group above the list would mean NIFTY
 * appearing twice, or a list that is missing one of its own members; it is
 * the same list, ordered by what gets traded.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const PINNED = ['NIFTY', 'BANKNIFTY', 'SENSEX'] as const

/** Lower sorts first. Everything unpinned keeps the order it arrived in. */
const rank = (underlying: string): number => {
  const at = PINNED.indexOf(underlying as (typeof PINNED)[number])
  return at === -1 ? PINNED.length : at
}

type Tab = 'ALL' | 'INDEX' | 'STOCK'

export interface InstrumentPickerProps {
  value: string
  onChange: (underlying: string) => void
  /** Recently opened, most recent first. */
  recent: readonly string[]
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'INDEX', label: 'Indices' },
  { id: 'STOCK', label: 'F&O stocks' },
]

export function InstrumentPicker({ value, onChange, recent }: InstrumentPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [tab, setTab] = useState<Tab>('ALL')
  const [items, setItems] = useState<UnderlyingDto[]>([])
  const [cursor, setCursor] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fetched once, when first opened. The catalogue does not change during a
  // session, and nobody pays for it who never opens the picker.
  useEffect(() => {
    if (!open || items.length > 0) return
    let cancelled = false
    void api
      .get<{ items: UnderlyingDto[] }>('/instruments/underlyings')
      .then((r) => {
        if (!cancelled) setItems(r.items)
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
    return () => {
      cancelled = true
    }
  }, [open, items.length])

  useEffect(() => {
    if (open) inputRef.current?.focus()
    else {
      setQuery('')
      setCursor(0)
    }
  }, [open])

  // Click-away and Escape. A picker that can only be closed by choosing
  // something makes the chain behind it unreachable.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  // Ctrl/Cmd-K opens it from anywhere, as the button advertises.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items
      .filter((x) => !INDICES_ONLY || x.kind === 'INDEX')
      .filter((x) => INDICES_ONLY || tab === 'ALL' || x.kind === tab)
      .filter((x) => !q || x.underlying.toLowerCase().includes(q) || nameOf(x).toLowerCase().includes(q))
      .sort((a, b) => rank(a.underlying) - rank(b.underlying))
      .slice(0, 60)
  }, [items, tab, query])

  useEffect(() => setCursor(0), [query, tab])

  const current = items.find((x) => x.underlying === value)

  const choose = (sym: string): void => {
    onChange(sym)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => Math.min(c + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => Math.max(c - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = results[cursor]
      if (pick) choose(pick.underlying)
    }
  }

  return (
    /*
     * The WRAPPER carries the sizing, not the button: the wrapper is what the
     * row lays out, and sizing only the button inside it left the control at
     * its content width whatever the rule said.
     */
    <div className="tr-pickerwrap relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Change instrument"
        aria-expanded={open}
        data-testid="instrument-picker"
        className="tr-picker"
      >
        <Search size={15} className="shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-col items-start leading-[1.15]">
          {/*
            A PLACEHOLDER WHEN NOTHING IS CHOSEN — ROADMAP 9.67. The basket
            builder opens this with no instrument yet, and an empty bold line
            above "Option chain" reads as a control that failed to load
            rather than one waiting to be used.
          */}
          <span
            className={cn(
              'truncate text-[14px] font-semibold',
              value === '' && 'font-normal text-muted-foreground',
            )}
          >
            {value === '' ? 'Search an instrument' : value}
          </span>
          <span className="truncate text-[11px] text-muted-foreground">
            {nameOf(current) || (current ? current.exchange : 'Option chain')}
          </span>
        </span>
        <span className="flex-1" />
        <span className="tr-kbd num">Ctrl K</span>
      </button>

      {open && (
        <div className="tr-pop" style={{ width: 520 }} data-testid="instrument-pop">
          <div className="flex h-[52px] items-center gap-2.5 border-b border-border px-3.5">
            <Search size={16} className="text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Search instruments"
              placeholder={INDICES_ONLY ? 'Search an index — NIFTY, BANKNIFTY, SENSEX…' : 'Search index or F&O stock — NIFTY, RELIANCE…'}
              data-testid="instrument-search"
              className="flex-1 border-0 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
            />
            <button type="button" onClick={() => setOpen(false)} className="tr-kbd num">
              Esc
            </button>
          </div>

          {/* A filter with one option is furniture — see the header. */}
          {!INDICES_ONLY && (
            <div className="flex gap-1.5 px-3.5 pb-1 pt-2.5">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`picker-tab-${t.id}`}
                  className={cn('tr-chip', tab === t.id && 'on')}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {recent.length > 0 && query === '' && (
            <div className="flex flex-wrap items-center gap-2 px-3.5 pb-0.5 pt-2">
              <span className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground">
                RECENT
              </span>
              {recent.map((r) => (
                <button key={r} type="button" onClick={() => choose(r)} className="tr-recent num">
                  {r}
                </button>
              ))}
            </div>
          )}

          <div className="flex max-h-[300px] flex-col gap-0.5 overflow-y-auto px-2 pb-1 pt-2.5">
            <span className="px-1.5 pb-1 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground">
              {query ? 'RESULTS' : INDICES_ONLY ? 'INDICES' : 'ALL INSTRUMENTS'}
            </span>
            {results.map((r, i) => (
              <button
                key={r.underlying}
                type="button"
                onMouseEnter={() => setCursor(i)}
                onClick={() => choose(r.underlying)}
                data-testid={`picker-item-${r.underlying}`}
                className={cn('tr-result', (i === cursor || r.underlying === value) && 'on')}
              >
                <span className="tr-ini num">{r.underlying.slice(0, 2)}</span>
                <span className="flex min-w-0 flex-1 flex-col leading-[1.25]">
                  <span className="truncate text-[14px] font-semibold">{r.underlying}</span>
                  <span className="truncate text-[12px] text-muted-foreground">{nameOf(r)}</span>
                </span>
                {!INDICES_ONLY && (
                  <span className="text-[12px] text-muted-foreground">
                    {r.kind === 'INDEX' ? 'Index' : 'Stock'}
                  </span>
                )}
                <span className={cn('tr-ex', r.exchange === 'BSE' && 'bse')}>{r.exchange}</span>
              </button>
            ))}
            {items.length > 0 && results.length === 0 && (
              <div className="px-2.5 py-4 text-[13px] text-muted-foreground">
                {/*
                  SAYS THE STOCKS ARE HIDDEN, rather than only that nothing
                  matched. A trader who types RELIANCE and reads "no
                  instrument matches" would conclude it is not listed, which
                  is false — it is listed and this picker is not showing it.
                  A filter the reader cannot see has to explain itself.
                */}
                No index matches &ldquo;{query}&rdquo;.
                {INDICES_ONLY && ' F&O stocks are hidden for now.'}
              </div>
            )}
            {items.length === 0 && (
              <div className="px-2.5 py-4 text-[13px] text-muted-foreground">{INDICES_ONLY ? 'Loading indices…' : 'Loading instruments…'}</div>
            )}
          </div>

          <div className="flex gap-4 border-t border-border px-3.5 py-2.5 text-[11px] text-muted-foreground">
            <span>↑ ↓ navigate</span>
            <span>↵ open chain</span>
            <span>Esc close</span>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The readable name.
 *
 * Indices have one in the shared spec. A stock underlying does NOT — the
 * catalogue stores the symbol, and its company name is not a field we hold.
 * Empty rather than a prettified guess.
 *
 * TODO: `Instrument` has no company-name column. Adding one to the sync
 * would fill this in with no change here.
 */
const nameOf = (x: UnderlyingDto | undefined): string =>
  x ? (INDEX_SPECS[x.underlying]?.name ?? '') : ''

